/**
 * How a block reads — `docs/activity.md` §6.2 (Board 4 C, the same
 * everywhere): a site visit or meeting with its time row, drive note, title,
 * the city line, the job and the tags as its height allows; a timed task's
 * amber chip; a time block's hatching; and the forecast in a day header
 * (§6.9).
 */
import type { CSSProperties, ReactNode } from 'react'
import {
  KIND_STYLES, RAIN_BLUE_FROM, WEATHER_COLOURS, WEATHER_TITLES, toneOf,
  type CalendarDrive, type CalendarItem, type DayForecast,
} from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Glyph } from '../glyphs.js'
import { FaceStack } from '../faces.js'
import { BLOCK_GAP, HATCH, TASK_BOX, TASK_LINE, TASK_TEXT, span } from './look.js'

const DRIVE_COLOUR: Record<CalendarDrive['tone'], string> = {
  ok: 'text-text-faint font-semibold',
  tight: 'text-warn font-bold',
  short: 'text-danger font-bold',
}

/** The drive note in a block's time row (§6.3): house from the start, car between stops. */
export function DriveNote({ drive, compact = false }: { drive: CalendarDrive; compact?: boolean }) {
  return (
    <span title={drive.tooltip} className={cn('ml-auto inline-flex shrink-0 items-center gap-[3px]', DRIVE_COLOUR[drive.tone])}>
      <Glyph name={drive.from === 'start' ? 'house' : 'car'} size={10} />
      {compact ? `${drive.minutes}m` : drive.note}
    </span>
  )
}

/**
 * A phone's held finger has it (Aaron, Oct 27, note 1.1.4): `drag` while the
 * finger carries it — a shadow, the scale is the caller's —, `edit` in edit
 * mode — ringed, its handles beside it —, `held` dropped and waiting on its
 * question or its save.
 */
export type LiftedLook = 'drag' | 'edit' | 'held'

const LIFTED: Record<LiftedLook, string> = {
  drag: 'shadow-popover',
  edit: 'shadow-popover ring-2 ring-accent touch-none',
  held: 'ring-2 ring-accent',
}

export function cityOf(item: CalendarItem): string | null {
  if (!item.place?.city) return null
  return item.place.state ? `${item.place.city}, ${item.place.state}` : item.place.city
}

export interface StopBlockProps {
  item: CalendarItem
  drive?: CalendarDrive | null
  /** The person's colour when several are shown; else the kind's (§6.2). */
  edge?: string
  /** Combined: the fill is a light shade of the person (§6.4.2). */
  fill?: string
  /** Combined: a shared meeting's edge is striped (Board 18 C). */
  stripes?: string[]
  /** A lane or a phone column under 130 px: start time only, city on two lines. */
  narrow?: boolean
  /** A phone's narrow column keeps the tags where the block has room (Board 13 B); a lane does not (Board 18 B). */
  narrowTags?: boolean
  /** Combined: the person's 15 px initials start the time row. */
  lead?: ReactNode
  /** Combined over several days: no job line, no tags (Board 18 A). */
  bare?: boolean
  /** Combined: no tags at any width (Board 18). */
  tags?: boolean
  showFaces?: boolean
  style?: CSSProperties
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
  /** A press: a drag, or on the top or bottom edge a resize (the caller decides by where). */
  onPointerDown?: (event: React.PointerEvent<HTMLElement>) => void
  /** Opens the thing itself (Outlook's double-click). */
  onDoubleClick?: () => void
  /** Delete or Backspace on it offers to remove it; Shift+F10 or the context-menu key opens its menu. */
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void
  /** A right-click: its menu (Aaron, Oct 24, note 1.7). */
  onContextMenu?: (event: React.MouseEvent<HTMLElement>) => void
  /** Its edges resize it (the viewer may change it); a press that is only a held finger's menu does not. */
  resizable?: boolean
  dragging?: boolean
  /** Being dragged: *moving…* under it (Board 6 B). */
  moving?: boolean
  /** Pixels a minute (`GRID_METRICS`). */
  scale?: number
  /** Lifted by a phone's held finger (note 1.1.4). */
  lifted?: LiftedLook
}

/** A site visit or a meeting (§6.2). */
export function StopBlock({
  item, drive, edge, fill, stripes, narrow = false, narrowTags = false, lead, bare = false, tags = true, showFaces = true, style,
  onClick, onPointerDown, onDoubleClick, onKeyDown, onContextMenu, resizable = Boolean(onPointerDown), dragging, moving, scale = 1,
  lifted,
}: StopBlockProps) {
  const height = (item.end - item.start) * scale - BLOCK_GAP
  const kind = item.kind === 'appointment' ? KIND_STYLES.site_visit : KIND_STYLES.meeting
  const city = cityOf(item)
  const short = height < 34
  const faces = showFaces && item.people.length > 1
  return (
    <button
      type="button"
      onClick={onClick}
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
      onKeyDown={onKeyDown}
      onContextMenu={onContextMenu}
      aria-haspopup={onContextMenu ? 'menu' : undefined}
      data-stop={item.key}
      data-lifted={lifted}
      className={cn(
        /* The boards' blocks inherit `line-height: normal`; the app's 1.5 clipped a 52 px block's city. */
        'absolute overflow-hidden rounded-[7px] border border-line text-left leading-[normal]',
        /* A button centres what it holds; a block reads from its top (D4) — and a line that does not fit is cut, never squashed. */
        'flex flex-col justify-start [&>*]:shrink-0',
        narrow ? 'py-1 pl-[9px] pr-1.5' : 'py-1 pl-2.5 pr-[7px]',
        dragging && 'opacity-90 shadow-popover',
        lifted && LIFTED[lifted],
      )}
      style={{ height, background: fill ?? 'rgb(var(--color-surface))', zIndex: 1, ...style }}
    >
      {/* Being dragged it is a dashed outline and nothing else: no edge, no tags (Board 6 B). */}
      {moving ? null : stripes && stripes.length > 1 ? (
        <i className="absolute inset-y-0 left-0 flex w-1 flex-col" aria-hidden>
          {stripes.map((colour, index) => <span key={index} className="flex-1" style={{ background: colour }} />)}
        </i>
      ) : (
        <i className="absolute inset-y-0 left-0 w-1" style={{ background: edge ?? kind.fg }} aria-hidden />
      )}
      {short ? (
        <span className="flex items-center gap-1 whitespace-nowrap text-[11px] font-semibold">
          <b className="truncate text-[11.5px]">{item.title}</b>
          {city && <span className="truncate font-semibold text-text-body">{city}</span>}
        </span>
      ) : (
        <>
          <span className="tnum flex items-center gap-1 whitespace-nowrap text-[10.5px] font-semibold text-text-muted">
            {lead}
            {narrow ? span(item.start, item.end).split('–')[0] : span(item.start, item.end)}
            {drive && <DriveNote drive={drive} compact={narrow} />}
          </span>
          <span className="mt-px flex items-center gap-1">
            <b className="block min-w-0 flex-1 truncate text-[12px] leading-[1.25]">{item.title}</b>
            {faces && <FaceStack people={item.people} size={narrow ? 15 : 17} max={narrow ? 2 : 6} />}
          </span>
          {city && !item.remote && (
            <span className={cn('mt-px flex gap-[3px] text-[11px] font-semibold text-text-body',
              narrow && height >= 64 ? 'items-start' : 'items-center whitespace-nowrap')}>
              {!narrow && <Glyph name="pin" size={10} />}
              {/* Board 13 B: the city on two lines where the block has room, else cut short. */}
              <span className={narrow && height >= 64 ? 'line-clamp-2' : 'truncate'}>{city}</span>
            </span>
          )}
          {moving && <span className="mt-px block text-[10.5px] text-text-muted">moving…</span>}
          {/* Board 12 A: 1 h 15 min at the iPad's 0.9 (66 px) still says what it is for. */}
          {!moving && !narrow && !bare && height >= 64 && item.sub && (
            <span className="mt-px block truncate text-[10.5px] text-text-muted">{item.sub}</span>
          )}
          {/* Board 12 A's 79 px Renee and Board 13 B's narrow one both carry *Confirmed*. */}
          {tags && !bare && !moving && (narrow ? narrowTags && height >= 86 : height >= 78) && item.tags.length > 0 && (
            <span className="mt-[3px] flex gap-[3px]">
              {item.tags.map((tag) => (
                <span key={tag} className="inline-flex h-4 items-center rounded px-1.5 text-[9.5px] font-semibold bg-accent-soft text-accent-dark">
                  {tag}
                </span>
              ))}
            </span>
          )}
        </>
      )}
      {/* Last, so the block's first line is still its first child. */}
      {resizable && !moving && <ResizeEdges />}
    </button>
  )
}

/** The top and bottom 6 px of a block that can be resized show the resize cursor (Outlook). */
function ResizeEdges() {
  return (
    <>
      <span aria-hidden data-resize="start" className="absolute inset-x-0 top-0 z-[2] h-1.5 cursor-ns-resize" />
      <span aria-hidden data-resize="end" className="absolute inset-x-0 bottom-0 z-[2] h-1.5 cursor-ns-resize" />
    </>
  )
}

/** A task with a time: a 22 px chip, its box ticks it (§6.2, §8.4). */
export function TaskChip({
  item, style, onTick, onClick, lead, onKeyDown, onContextMenu, onPointerDown, lifted,
}: {
  item: CalendarItem; style?: CSSProperties; onTick?: () => void
  onClick?: (event: React.MouseEvent<HTMLElement>) => void; lead?: ReactNode
  /** Delete or Backspace on it offers to remove it; Shift+F10 or the context-menu key opens its menu. */
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void
  /** A right-click: its menu (note 1.7). */
  onContextMenu?: (event: React.MouseEvent<HTMLElement>) => void
  /** A finger held on it: its menu, on an iPad — on a phone, it lifts it to move it (note 1.1.4). */
  onPointerDown?: (event: React.PointerEvent<HTMLElement>) => void
  lifted?: LiftedLook
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      data-task={item.key}
      data-lifted={lifted}
      aria-haspopup={onContextMenu ? 'menu' : undefined}
      onContextMenu={onContextMenu}
      onPointerDown={onPointerDown}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onClick?.(event as unknown as React.MouseEvent<HTMLElement>)
        else onKeyDown?.(event)
      }}
      className={cn('absolute flex h-[22px] items-center gap-[5px] overflow-hidden whitespace-nowrap rounded-md border bg-surface px-1.5 text-[11px] font-semibold',
        item.done ? 'text-text-faint line-through' : '', lifted && LIFTED[lifted])}
      style={{ borderColor: TASK_LINE, color: item.done ? undefined : TASK_TEXT, zIndex: 3, ...style }}
    >
      <button
        type="button"
        aria-label={item.done ? 'Mark not done' : 'Mark done'}
        onClick={(event) => { event.stopPropagation(); onTick?.() }}
        className="inline-flex h-[11px] w-[11px] shrink-0 items-center justify-center rounded-[3px] border-[1.5px] text-white"
        style={{ borderColor: TASK_BOX, background: item.done ? TASK_BOX : 'rgb(var(--color-surface))' }}
      >
        {item.done && <Glyph name="check" size={8} stroke={3} />}
      </button>
      {lead}
      <span className="tnum truncate">{span(item.start, item.end).split('–')[0]} {item.title}</span>
    </div>
  )
}

/** A time block — hatched, a hairline, no edge (in Combined: the person's edge). */
export function TimeBlock({
  item, edge, style, onClick, lead, scale = 1, narrow = false, onPointerDown, onKeyDown, onContextMenu,
  resizable = Boolean(onPointerDown), lifted,
}: {
  item: CalendarItem; edge?: string; style?: CSSProperties; lead?: ReactNode
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
  /** A press: a drag, or on the top or bottom edge a resize. */
  onPointerDown?: (event: React.PointerEvent<HTMLElement>) => void
  /** Delete or Backspace on it offers to remove it; Shift+F10 or the context-menu key opens its menu. */
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void
  /** A right-click: its menu (note 1.7). */
  onContextMenu?: (event: React.MouseEvent<HTMLElement>) => void
  /** Its edges resize it; a press that is only a held finger's menu does not. */
  resizable?: boolean
  scale?: number
  /** A phone's narrow column: the start time only, as Board 13 B draws *3:00 · Office*. */
  narrow?: boolean
  /** Lifted by a phone's held finger (note 1.1.4). */
  lifted?: LiftedLook
}) {
  const height = (item.end - item.start) * scale - BLOCK_GAP
  const one = height < 34
  return (
    <button
      type="button"
      onClick={onClick}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      onContextMenu={onContextMenu}
      aria-haspopup={onContextMenu ? 'menu' : undefined}
      data-block={item.key}
      data-lifted={lifted}
      className={cn('absolute flex flex-col justify-start overflow-hidden [&>*]:shrink-0 rounded-[7px] border border-line px-[7px] py-1 text-left leading-[normal]',
        lifted && LIFTED[lifted])}
      style={{ height, background: HATCH, ...(edge ? { paddingLeft: 10 } : {}), ...style }}
    >
      {edge && <i className="absolute inset-y-0 left-0 w-1" style={{ background: edge }} aria-hidden />}
      {one ? (
        <span className="tnum flex items-center gap-1.5 whitespace-nowrap text-[10.5px] font-semibold text-text-muted">
          {lead}{span(item.start, item.end).split('–')[0]}{' '}<b className="truncate text-[11.5px] font-bold text-text-body">{item.title}</b>
        </span>
      ) : (
        <>
          <span className="tnum flex items-center gap-1 text-[10.5px] font-semibold text-text-muted">
            {lead}{narrow ? span(item.start, item.end).split('–')[0] : span(item.start, item.end)}
          </span>
          <b className="block truncate text-[11.5px] font-bold text-text-body">{item.title}</b>
        </>
      )}
      {resizable && <ResizeEdges />}
    </button>
  )
}

/** A day header's forecast (§6.9): icon, high bold, low faint, a drop and the rain chance. */
export function Forecast({ weather, iconOnly = false, highOnly = false, textSize = 11, iconSize = 14, dropSize = 10 }: {
  weather: DayForecast; iconOnly?: boolean; highOnly?: boolean
  /** The words' size: 11 in a day header, 12 on the phone's day line, 12.5 in the iPad's toolbar (Boards 12, 13). */
  textSize?: number
  /** The sky and the drop: 14 and 10 in a day header, 15 and 11 in the desktop's Day toolbar (Board 3). */
  iconSize?: number
  dropSize?: number
}) {
  const rainBlue = weather.rain >= RAIN_BLUE_FROM
  return (
    <span data-forecast="" className="inline-flex items-center gap-[5px] whitespace-nowrap leading-none"
      style={{ fontSize: textSize }}>
      <span style={{ color: WEATHER_COLOURS[weather.icon] }} className="inline-flex">
        <Glyph name={weather.icon} size={iconSize} title={WEATHER_TITLES[weather.icon]} />
      </span>
      {!iconOnly && weather.high !== null && <b className="font-bold text-text">{weather.high}°</b>}
      {!iconOnly && !highOnly && weather.low !== null && (
        <span className="-ml-0.5 font-semibold text-text-faint">{weather.low}°</span>
      )}
      {!iconOnly && !highOnly && (
        <span className={cn('inline-flex items-center gap-0.5 font-semibold', rainBlue ? 'text-info' : 'text-text-faint')}>
          <Glyph name="drop" size={dropSize} />{weather.rain}%
        </span>
      )}
    </span>
  )
}

/** The person's tone for Combined (§6.4.2): the edge, and its light shade for the fill. */
export function personFill(colour: string | null): { edge: string; fill: string } {
  const tone = toneOf(colour)
  return { edge: tone.edge, fill: tone.tint }
}
